import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Monitor, Baby, MapPin, MessageCircle, ShieldAlert, LogOut, Send, BarChart3, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 5000;

const TRIAGE_STYLE = {
  high: "border-[#D5281B] text-[#D5281B]",
  medium: "border-[#ED8B00] text-[#ED8B00]",
  low: "border-emerald-500 text-emerald-400",
};

const fmtTime = (dt) =>
  dt ? new Date(dt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";

function CasualtyCard({ b, testid }) {
  return (
    <div className={`group relative rounded-xl border-2 bg-[#0F2547]/60 p-3 ${TRIAGE_STYLE[b.triage] || TRIAGE_STYLE.low}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="font-heading font-extrabold text-white text-sm">{b.ref}</p>
        <p className="text-[10px] font-bold uppercase tracking-widest">{b.triage} priority</p>
      </div>
      <p className="text-xs text-slate-300 mt-1">{b.injuries.length ? b.injuries.join(", ") : "No injury recorded"}</p>
      {b.note && (
        <div className="hidden group-hover:block absolute left-0 bottom-full z-30 mb-1 w-72 rounded-xl bg-white text-[#0F2547] border border-slate-200 shadow-xl p-3" data-testid={`${testid}-note`}>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Latest note · {b.note.author} · {fmtTime(b.note.at)}</p>
          <p className="text-xs whitespace-pre-wrap">{b.note.text}</p>
        </div>
      )}
    </div>
  );
}

export default function Wall() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  const tick = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/event-wall/${token}`, { credentials: "omit" });
      if (!r.ok) throw new Error();
      setData(await r.json());
    } catch {
      setError("This wall display link is not valid or has expired.");
    }
  }, [token]);

  useEffect(() => {
    tick();
    const t = setInterval(tick, REFRESH_MS);
    return () => clearInterval(t);
  }, [tick]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.chat?.length]);

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    setSending(true);
    try {
      await fetch(`${API}/public/event-wall/${token}/message`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: chatInput.trim() }),
      });
      setChatInput("");
      tick();
    } finally {
      setSending(false);
    }
  };

  const deleteMsg = async (id) => {
    try {
      await fetch(`${API}/public/event-wall/${token}/message/${id}`, { method: "DELETE", credentials: "omit" });
      tick();
    } catch (e) {
      console.debug("Wall message delete failed; next refresh resyncs:", e?.message || e);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center p-6">
        <p className="text-white font-heading font-bold text-xl" data-testid="wall-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center">
        <div className="h-12 w-12 rounded-full border-4 border-white border-t-transparent animate-spin" />
      </div>
    );
  }

  const grouped = {};
  data.bays.forEach((b) => {
    (grouped[b.location] = grouped[b.location] || []).push(b);
  });
  const locations = Object.keys(grouped).sort((a, b) => (a === "Unassigned" ? 1 : b === "Unassigned" ? -1 : a.localeCompare(b)));
  const maxArrivals = Math.max(1, ...data.arrivals_by_hour.map((a) => a.count));
  const mapQuery = [data.venue, data.event].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-[#0F2547] text-white flex flex-col" data-testid="wall-display">
      <header className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <Monitor className="h-7 w-7 text-sky-300" />
          <div>
            <h1 className="font-heading font-extrabold text-xl sm:text-2xl leading-tight" data-testid="wall-event-name">{data.event}</h1>
            <p className="text-xs text-slate-300">{data.venue}{data.venue && data.event_date && " · "}{data.event_date}</p>
          </div>
        </div>
        <div className="flex items-center gap-8">
          <div className="text-right">
            <p className="font-vitals font-extrabold text-3xl sm:text-4xl text-sky-300" data-testid="wall-active-count">{data.bays.length}</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-300">On site now</p>
          </div>
          <div className="text-right">
            <p className="font-vitals font-extrabold text-3xl sm:text-4xl text-emerald-400" data-testid="wall-out-count">{data.signed_out.length}</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-300">Signed out</p>
          </div>
        </div>
      </header>

      {data.lost_with_us > 0 && (
        <div className="mx-6 sm:mx-10 mt-3 rounded-xl bg-[#ED8B00] text-[#0F2547] px-5 py-2.5 font-heading font-extrabold text-sm flex items-center gap-2 shrink-0" data-testid="wall-lost-alert">
          <Baby className="h-5 w-5" /> LOST CHILDREN WITH US: {data.lost_with_us} — see control for details
        </div>
      )}

      <div className="grid grid-cols-5 gap-2 px-6 sm:px-10 pt-3 shrink-0" data-testid="wall-stats-strip">
        {[
          ["Lost children", data.lost_with_us, "text-[#ED8B00]", "wall-stat-lost"],
          ["With you now", data.counts.active, "text-sky-300", "wall-stat-active"],
          ["High priority", data.counts.high, "text-[#D5281B]", "wall-stat-high"],
          ["Discharged", data.counts.discharged, "text-emerald-400", "wall-stat-discharged"],
          ["Treated", data.counts.treated, "text-white", "wall-stat-treated"],
        ].map(([label, val, cls, testid]) => (
          <div key={testid} className="rounded-xl bg-[#173766]/60 border border-slate-700/60 px-3 py-2 text-center">
            <p className={`font-vitals font-extrabold text-xl sm:text-2xl ${cls}`} data-testid={testid}>{val}</p>
            <p className="text-[8px] sm:text-[9px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex-1 flex flex-col xl:flex-row min-h-0">
        <main className="flex-1 p-6 sm:p-8 overflow-y-auto">
          {locations.length === 0 ? (
            <div className="text-center py-10" data-testid="wall-empty">
              <p className="font-heading font-extrabold text-2xl text-slate-300">No casualties currently on site</p>
              <p className="text-sm text-slate-400 mt-2">This board updates automatically every 5 seconds.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
              {locations.map((loc) => (
                <section key={loc} className="rounded-2xl bg-[#173766]/60 border border-slate-700/60 p-4" data-testid={`wall-bay-${loc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h2 className="font-heading font-extrabold text-sm sm:text-base uppercase tracking-widest text-sky-300 mb-2.5 flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> {loc}
                    <span className="ml-auto font-vitals text-white">{grouped[loc].length}</span>
                  </h2>
                  <div className="space-y-2">
                    {grouped[loc].map((b) => (
                      <CasualtyCard key={b.ref} b={b} testid={`wall-casualty-${b.ref.replace(/\s/g, "-")}`} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {data.signed_out.length > 0 && (
            <section className="mt-6" data-testid="wall-signed-out">
              <h2 className="font-heading font-extrabold text-sm uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-2">
                <LogOut className="h-4 w-4" /> Signed out ({data.signed_out.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.signed_out.map((b) => (
                  <CasualtyCard key={b.ref} b={b} testid={`wall-out-${b.ref.replace(/\s/g, "-")}`} />
                ))}
              </div>
            </section>
          )}

          {data.arrivals_by_hour.length > 0 && (
            <section className="mt-6 rounded-2xl bg-[#173766]/60 border border-slate-700/60 p-4" data-testid="wall-arrivals-graph">
              <h2 className="font-heading font-extrabold text-sm uppercase tracking-widest text-sky-300 mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> Arrivals per hour
              </h2>
              <div className="flex items-end gap-2 h-28">
                {data.arrivals_by_hour.map((a) => (
                  <div key={a.hour} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <span className="font-vitals font-bold text-xs text-white">{a.count}</span>
                    <div className="w-full rounded-t-md bg-[#005EB8]" style={{ height: `${(a.count / maxArrivals) * 72}px` }} />
                    <span className="text-[9px] text-slate-400 font-semibold">{a.hour}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>

        <aside className="xl:w-[380px] shrink-0 border-t xl:border-t-0 xl:border-l border-white/10 flex flex-col bg-[#0d1f3c] max-h-[60vh] xl:max-h-none" data-testid="wall-chat">
          <div className="px-5 py-3.5 border-b border-white/10 flex items-center gap-2 shrink-0">
            <MessageCircle className="h-5 w-5 text-sky-300" />
            <h2 className="font-heading font-extrabold text-sm uppercase tracking-widest">Live chat</h2>
            <span className="ml-auto h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="wall-chat-messages">
            {data.chat.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No messages yet — staff messages and alerts appear here live.</p>
            ) : (
              data.chat.map((m, i) =>
                m.kind === "alert" ? (
                  <div key={m.id} className="group relative rounded-xl bg-red-600 px-3 py-2.5" data-testid={`wall-alert-${i}`}>
                    <p className="text-[9px] font-extrabold uppercase tracking-widest flex items-center gap-1 mb-0.5">
                      <ShieldAlert className="h-3 w-3" /> Alert — {m.sender} · {fmtTime(m.created_at)}
                    </p>
                    <p className="text-xs font-bold pr-6">{m.text}</p>
                    <button onClick={() => deleteMsg(m.id)} data-testid={`wall-msg-del-${i}`} title="Delete this message"
                      className="absolute top-1.5 right-1.5 p-1 rounded-md text-white/60 hover:text-white hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div key={m.id} className="group relative rounded-xl bg-[#173766]/80 border border-slate-700/50 px-3 py-2" data-testid={`wall-chat-msg-${i}`}>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-sky-300">{m.sender} · {fmtTime(m.created_at)}</p>
                    <p className="text-xs text-slate-100 mt-0.5 whitespace-pre-wrap pr-6">{m.text}</p>
                    <button onClick={() => deleteMsg(m.id)} data-testid={`wall-msg-del-${i}`} title="Delete this message"
                      className="absolute top-1.5 right-1.5 p-1 rounded-md text-slate-500 hover:text-[#D5281B] hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )
              )
            )}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex gap-2 shrink-0">
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Message staff as Control…"
              data-testid="wall-chat-input"
              className="flex-1 h-10 rounded-lg bg-[#173766] border border-slate-600 px-3 text-xs text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
            <button type="submit" disabled={sending || !chatInput.trim()} data-testid="wall-chat-send"
              className="h-10 w-10 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors grid place-items-center disabled:opacity-50 shrink-0">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </aside>
      </div>

      <footer className="px-6 sm:px-10 py-2.5 border-t border-white/10 flex justify-between text-[10px] uppercase tracking-widest text-slate-400 shrink-0">
        <span>Wales Event Medical Services · Control wall</span>
        <span>Updated {new Date(data.generated_at).toLocaleTimeString("en-GB")} · auto-refresh 5s · anonymised</span>
      </footer>
    </div>
  );
}
